import { useState } from "react";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { EmptyState } from "@/components/ui/EmptyState";
import { Panel } from "@/components/ui/Panel";
import { AlertTriangle } from "lucide-react";
import type { FileResponse, LiteratureType } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { Menu, MenuItem } from "@/components/ui/Menu";
import { cn } from "@/lib/utils";
import {
  hiddenCount,
  libraryCountLabel,
  liveSelection,
  visibleRows,
} from "./prior-art-list";
import { LITERATURE_TYPES, PriorArtRow } from "./PriorArtRow";
import { excludedAsUnreadable } from "./prior-art-status";

/**
 * Which documents the analysis will not read, named before the run so an
 * unreadable document is never mistaken for an assessed one.
 */
function ExcludedSummary({ files }: { files: readonly FileResponse[] }) {
  const excluded = excludedAsUnreadable(files);
  if (excluded.length === 0) return null;
  return (
    <Panel
      as="p"
      shape="row"
      pad="tight"
      tone="destructive"
      data-testid="prior-art-excluded"
      className="mt-2 text-[11.5px]"
    >
      {excluded.length === 1
        ? "1 document excluded as unreadable: "
        : `${excluded.length} documents excluded as unreadable: `}
      {excluded
        .map((f) =>
          f.reference_id === null
            ? f.original_filename
            : `${f.reference_id} · ${f.original_filename}`,
        )
        .join(", ")}
      . Replace or delete them; the analysis reads the rest.
    </Panel>
  );
}

/**
 * The bar that appears once rows are ticked: clear, set one type the attorney picks for every
 * ticked document, or delete them. The type is the attorney's choice for the documents they
 * ticked, never one the page guesses.
 */
function BulkBar({
  count,
  onClear,
  onType,
  onDelete,
}: {
  count: number;
  onClear: () => void;
  onType: (value: LiteratureType) => void;
  onDelete: () => void;
}) {
  return (
    <Panel
      shape="row"
      pad="tight"
      data-testid="prior-art-bulk"
      className="mb-2 flex items-center gap-2 border-primary/20 bg-primary/[0.04] py-1.5 text-[12px]"
    >
      <span className="flex-1 font-semibold">{count} selected</span>
      <Button
        type="button"
        size="dense"
        variant="quiet"
        onClick={onClear}
        className="cursor-pointer"
      >
        Clear selection
      </Button>
      <Menu name="Set type" trigger={{ content: "Set type" }} align="end">
        {LITERATURE_TYPES.map((type) => (
          <MenuItem
            key={type.value}
            title={type.label}
            onSelect={() => onType(type.value)}
          />
        ))}
      </Menu>
      <Button
        type="button"
        size="dense"
        variant="danger"
        onClick={onDelete}
        className="cursor-pointer"
      >
        Delete selected
      </Button>
    </Panel>
  );
}

/** What every row is handed: who it belongs to and what it may do. */
interface RowActions {
  taskId: string;
  accept: Record<string, string[]>;
  /** Where the disclosure's account of a document differs from its text, by file id. */
  accounts: Readonly<Record<string, readonly string[]>>;
  onClassify: (fileId: string, value: LiteratureType | null) => void;
  onView: (file: FileResponse) => void;
  onDeleteOne: (file: FileResponse) => void;
  onReplace: (file: FileResponse, replacement: File) => Promise<void>;
}

/** The rows on screen and the control that reveals or hides the rest. */
function LibraryRows({
  files,
  expanded,
  selected,
  onExpand,
  onSelect,
  actions,
}: {
  files: readonly FileResponse[];
  expanded: boolean;
  selected: ReadonlySet<string>;
  onExpand: () => void;
  onSelect: (fileId: string, on: boolean) => void;
  actions: RowActions;
}) {
  const hidden = hiddenCount(files, expanded);
  return (
    <>
      {/* Expanded, the list scrolls inside its own box so the positions
          and the run action below stay reachable. */}
      <ul
        data-testid="prior-art-rows"
        data-expanded={expanded ? "true" : "false"}
        className={cn(expanded && "max-h-[420px] overflow-y-auto pr-1")}
      >
        {visibleRows(files, expanded).map((file) => (
          <PriorArtRow
            key={file.file_id}
            file={file}
            taskId={actions.taskId}
            accept={actions.accept}
            selected={selected.has(file.file_id)}
            disagreements={actions.accounts[file.file_id] ?? []}
            onSelect={onSelect}
            onClassify={actions.onClassify}
            onView={actions.onView}
            onDelete={actions.onDeleteOne}
            onReplace={actions.onReplace}
          />
        ))}
      </ul>
      {(hidden > 0 || expanded) && (
        <div className="mt-2 border-t border-border pt-2">
          <Button
            type="button"
            size="dense"
            variant="secondary"
            onClick={onExpand}
            className="w-full cursor-pointer"
          >
            {expanded ? "Show fewer" : `Show more (${hidden})`}
          </Button>
        </div>
      )}
    </>
  );
}

/** Why a delete was refused, said above the library it was asked of. */
function DeleteRefusal({ said }: { said: string | null }) {
  if (said === null) return null;
  return (
    <Panel
      as="p"
      shape="row"
      pad="tight"
      tone="destructive"
      role="alert"
      data-testid="prior-art-delete-error"
      className="mb-2 text-[11.5px]"
    >
      {said}
    </Panel>
  );
}

/**
 * The list under the upload area: the count, then every prior art document
 * on the task, or the empty state, or word that the list is on its way.
 *
 * The selection and the expanded state are the list's own: both are how the
 * attorney is looking at the list, not what is in it, and both reset on a
 * fresh entry to the tab.
 */
export function PriorArtLibrary({
  files,
  loading,
  deleteError,
  onDeleteMany,
  ...actions
}: RowActions & {
  files: readonly FileResponse[];
  loading: boolean;
  /** Why the last delete was refused, shown until the next one is asked for. */
  deleteError: string | null;
  onDeleteMany: (fileIds: string[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const selected = liveSelection(ticked, files);

  const select = (fileId: string, on: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(fileId);
      else next.delete(fileId);
      return next;
    });

  return (
    <div data-testid="prior-art-library" className="mt-4">
      <MicroLabel as="p" size="sm" className="mb-2">
        {libraryCountLabel(files.length)}
      </MicroLabel>
      <DeleteRefusal said={deleteError} />
      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onClear={() => setTicked(new Set())}
          onType={(value) => {
            for (const fileId of selected) actions.onClassify(fileId, value);
          }}
          onDelete={() => onDeleteMany([...selected])}
        />
      )}
      {loading ? (
        <p
          data-testid="prior-art-loading"
          className="text-[12px] text-muted-foreground"
        >
          Loading your prior art…
        </p>
      ) : files.length === 0 ? (
        <EmptyState
          as="p"
          shape="row"
          data-testid="prior-art-empty"
          className="justify-start py-2.5 text-left text-[12px]"
        >
          <AlertTriangle className="h-3.5 w-3.5 flex-none" aria-hidden />
          No prior art uploaded. Add at least one document; novelty cannot be
          assessed without it.
        </EmptyState>
      ) : (
        <>
          <LibraryRows
            files={files}
            expanded={expanded}
            selected={selected}
            onExpand={() => setExpanded((e) => !e)}
            onSelect={select}
            actions={actions}
          />
          <ExcludedSummary files={files} />
        </>
      )}
    </div>
  );
}
